import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, ArrowLeftRight, FileSearch, Cog, Key, GitBranch, Shield,
  CheckCircle2, ChevronDown, Globe, XCircle
} from 'lucide-react';
import { howItWorksPageContent, founderFAQ } from '../data/mock';

const iconMap = {
  FileSearch, Cog, Key, GitBranch, Shield
};

const HowItWorksPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-20 w-64 h-64 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1e3a5f]/10 text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ArrowLeftRight className="w-4 h-4" />
            <span>The Process</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] mb-6">
            {howItWorksPageContent.hero.headline}
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {howItWorksPageContent.hero.subheadline}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed text-center font-medium mb-4">
            {howItWorksPageContent.modelFraming}
          </p>
          <p className="text-lg text-slate-600 leading-relaxed text-center">
            {howItWorksPageContent.intro}
          </p>
        </div>
      </section>

      {/* Timeline Stages */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200 hidden md:block" />
            
            <div className="space-y-12">
              {howItWorksPageContent.stages.map((stage, idx) => {
                const Icon = iconMap[stage.icon];
                return (
                  <div key={idx} className="relative">
                    {/* Timeline dot */}
                    <div className="absolute left-8 -translate-x-1/2 w-4 h-4 rounded-full bg-[#1e3a5f] border-4 border-white shadow-md hidden md:block" />
                    
                    <Card className="ml-0 md:ml-20 bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
                      <CardContent className="p-8">
                        <div className="flex items-start space-x-4 mb-6">
                          <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-7 h-7 text-[#1e3a5f]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-emerald-600 mb-1">{stage.subtitle}</div>
                            <h3 className="text-xl font-bold text-[#0f1f2e]">{stage.title}</h3>
                          </div>
                        </div>
                        
                        <p className="text-slate-600 leading-relaxed mb-6">
                          {stage.description}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 gap-3">
                          {stage.keyPoints.map((point, pointIdx) => (
                            <div key={pointIdx} className="flex items-start space-x-2">
                              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-slate-600">{point}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Foreign Filings Note */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0f1f2e] mb-2">
                    {howItWorksPageContent.foreignFilings.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {howItWorksPageContent.foreignFilings.description}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What We Are Not */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0f1f2e] mb-8 text-center">
            {howItWorksPageContent.whatWeAreNot.title}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {howItWorksPageContent.whatWeAreNot.points.map((point, idx) => (
              <div key={idx} className="flex items-center space-x-3 bg-white p-4 rounded-xl border border-slate-200">
                <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <span className="text-slate-600">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              Founder FAQ
            </h2>
            <p className="text-slate-600">
              Straight answers for founders evaluating IP options.
            </p>
          </div>

          <div className="space-y-4">
            {founderFAQ.map((faq, idx) => (
              <Card 
                key={idx} 
                className={`bg-slate-50 border-slate-200 transition-all duration-300 ${openFAQ === idx ? 'shadow-lg border-[#1e3a5f]/20' : 'hover:bg-slate-100'}`}
              >
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFAQ(idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <h3 className="font-semibold text-[#0f1f2e] pr-4">{faq.question}</h3>
                    <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${openFAQ === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFAQ === idx && (
                    <div className="px-6 pb-6">
                      <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start?
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            Submit your concept for evaluation. We'll assess fit and discuss next steps.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-access">
              <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-slate-100 px-8 py-6 text-base font-semibold">
                Request Evaluation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/why-ipfunders">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base">
                Why IpFunders Exists
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
